import type { BaseQueue, BaseQueueGroup, BaseQueueStep } from 'types';
import { nodeIsDone } from '../../../../features/document/workflow/helpers';
import type { Workflow } from '../../model';

/**
 * This file contains helper functions for converting between queue and workflow types.
 */

/**
 * The delta value used to calculate position nodes in the workflow.
 */
export const WORKFLOW_POSITION_DELTA = 400;

/**
 * This function converts a queue object to a Workflow object.
 *
 * @param {BaseQueue} queue - The queue object to convert.
 * @param {BaseQueueGroup[]} groups - The group object to use for conversion.
 * @returns {Workflow} - The converted Workflow object.
 */
export const convertQueueToWorkflow = (
  queue: BaseQueue,
  groups: BaseQueueGroup[],
): Workflow => {
  const index = {
    accept: 0,
    signe: 0,
    hold: 0,
    edges: 0,
  };

  const result: Workflow = {
    edges: [],
    nodes: [],
  };

  /**
   * Get the index for the given type.
   * @param type - The type of node to get the index for.
   */
  const getIndex = (type: keyof typeof index): number => {
    const id = index[type];
    index[type] += 1;
    return id;
  };

  /**
   * Create a node for the given type and data.
   * @param type - The type of node to create.
   * @param data - The data for the node.
   */
  const createNode = (
    type: 'accept' | 'signe' | 'hold',
    data: any,
  ): Workflow['nodes'][number] => ({
    id: `${type}-${getIndex(type)}`,
    type,
    name: type,
    typeVersion: 1,
    parameters: data,
    position: [result.nodes.length * WORKFLOW_POSITION_DELTA, 0],
  });

  /**
   * Create an edge connecting the last two nodes in the workflow.
   */
  const createEdge = (): Workflow['edges'][number] => ({
    source: result.nodes[result.nodes.length - 2].id,
    target: result.nodes[result.nodes.length - 1].id,
  });

  /**
   * Get the groups for a specific step in the queue.
   * @param type - The type of group to get ('acceptor' or 'signer').
   * @param stepIndex - The index of the step in the queue.
   */
  const groupForStep = (
    type: 'accept' | 'signe',
    stepIndex: number,
  ): BaseQueueGroup[] => {
    const map = {
      accept: 'acceptor',
      signe: 'signer',
    };

    return groups.filter(
      group =>
        group.queueRole === map[type] && group.queueStep === stepIndex + 1,
    );
  };

  /**
   * Process a step in the queue and add it to the workflow.
   * @param type - The type of step to process ('accept' or 'signe').
   * @param data - The data for the step to process.
   * @param StepIndex - The index of the step in the queue.
   */
  const processQueueStep = (
    type: 'accept' | 'signe',
    data: BaseQueueStep,
    StepIndex: number,
  ) => {
    const groupsForStep = groupForStep(type, StepIndex);
    const usersIdInGroups = groupsForStep.flatMap(g =>
      g.users.map(u => u.userId),
    );

    const userIdInDocumentGroup = groupsForStep.flatMap(g =>
      g.users.map(u => u.documentUser?.id),
    );

    const participants: any[] = [];

    // convert groups to participants and apply user data in group
    groupsForStep.forEach(group => {
      const { users, ...restGroupProps } = group;

      participants.push({
        id: group.id,
        type: 'participant-group',
        parameters: {
          ...restGroupProps,
          participants: users.reduce<any>((acc, user) => {
            const userData = data.queue.find(
              p => p.user.id === user.userId || p.id === user.documentUser?.id,
            );

            if (userData) {
              return [
                ...acc,
                {
                  id: userData.id,
                  type: 'participant-user',
                  parameters: userData,
                },
              ];
            }

            return acc;
          }, []),
        },
      });
    });

    // and other participants
    data.queue.forEach(participant => {
      if (
        participant.user.id &&
        !usersIdInGroups.includes(participant.user.id) &&
        !userIdInDocumentGroup.includes(participant.id)
      ) {
        participants.push({
          id: participant.id,
          type: 'participant-user',
          parameters: participant,
        });
      }
    });

    const node = createNode(type, {
      participants,
    });

    node.status = nodeIsDone(node as any) ? 'done' : undefined;

    result.nodes.push(node);

    // If this is not the first node, create an edge to the previous node.
    if (result.nodes.length > 1) {
      result.edges.push(createEdge());
    }

    // If the step has a holdAfter property, add a hold node.
    if (data.holdAfter) {
      const holdNode = createNode('hold', {});
      result.nodes.push(holdNode);
      result.edges.push(createEdge());
    }
  };

  /**
   * Process a step in the queue and add it to the workflow.
   */
  queue.acceptors.forEach((acceptor, stepIndex) => {
    processQueueStep('accept', acceptor, stepIndex);
  });
  queue.signers.forEach((signer, stepIndex) => {
    processQueueStep('signe', signer, stepIndex);
  });

  return result;
};
